import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import MyDataConnectLayout from '../components/mydata/MyDataConnectLayout';
import { getMyDataDraft, patchMyDataDraft } from '../utils/myDataDraft';
import {
  nhisGetCollectAllJob,
  nhisGetLatestCollection,
  nhisLogout,
  nhisStartCollectAllJob,
  parseNhisError,
} from '../utils/nhisApi';

const POLL_INTERVAL_MS = 3_000;
const TERMINAL_STATUSES = new Set(['SUCCEEDED', 'FAILED', 'EXPIRED', 'CANCELED']);

const STATUS_LABELS = {
  PENDING: '수집 준비 중입니다',
  RUNNING: '의료 데이터를 수집하고 있습니다',
  SUCCEEDED: '수집을 마무리하고 있습니다',
  FAILED: '수집 중 오류가 발생했습니다',
  EXPIRED: '수집 요청이 만료되었습니다',
  CANCELED: '수집 요청이 취소되었습니다',
};

const STEP_ORDER = ['HEALTH_CHECKUP', 'TREATMENT_MEDICATION', 'MEDICAL_HISTORY', 'LOGOUT', 'COMPLETED'];

const COLLECT_ITEMS = [
  { label: '건강검진결과', step: 'HEALTH_CHECKUP' },
  { label: '진료/투약정보', step: 'TREATMENT_MEDICATION' },
  { label: '진료내역', step: 'MEDICAL_HISTORY' },
];

function normalizeJobSnapshot(job) {
  return {
    jobId: job?.jobId || '',
    status: job?.status || 'PENDING',
    currentStep: job?.currentStep || 'HEALTH_CHECKUP',
    progressPercent: Number.isFinite(Number(job?.progressPercent))
      ? Math.max(0, Math.min(100, Number(job.progressPercent)))
      : 0,
    errorCode: job?.errorCode || null,
    errorMessage: job?.errorMessage || null,
    startedAt: job?.startedAt || null,
    completedAt: job?.completedAt || null,
    updatedAt: job?.updatedAt || null,
  };
}

function getStepState(itemStep, currentStep, status, elapsed) {
  if (status === 'SUCCEEDED') return 'done';
  if (status === 'PENDING') {
    return itemStep === 'HEALTH_CHECKUP' ? 'active' : 'waiting';
  }

  const itemIndex = STEP_ORDER.indexOf(itemStep);
  const currentIndex = STEP_ORDER.indexOf(currentStep);
  if (currentIndex < 0 || itemIndex < 0) {
    return elapsed >= itemIndex * 3 ? 'active' : 'waiting';
  }
  if (itemIndex < currentIndex) return 'done';
  if (itemIndex === currentIndex) return 'active';
  return 'waiting';
}

function statusToErrorReason(status) {
  if (status === 'EXPIRED') return 'timeout';
  return 'server';
}

function getDisplayProgress({ status, currentStep, progressPercent }) {
  if (status === 'SUCCEEDED') return 100;
  if (Number(progressPercent) > 0) return progressPercent;

  const currentIndex = STEP_ORDER.indexOf(currentStep);
  if (currentIndex < 0) return status === 'RUNNING' ? 15 : 5;
  return Math.min(95, 10 + currentIndex * 25);
}

export default function MyDataFetchingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/app') ? '/app' : '';
  const [elapsed, setElapsed] = useState(0);
  const [jobSnapshot, setJobSnapshot] = useState(() => normalizeJobSnapshot(null));
  const startRef = useRef(Date.now());

  const tone = {
    surface: '#ffffff',
    surfaceMuted: '#f9fafb',
    border: '#e5e7eb',
    borderSoft: '#d1d5db',
    textStrong: '#111827',
    text: '#374151',
    textMuted: '#6b7280',
    primary: '#3498db',
    primarySoft: '#eff6ff',
    success: '#16a34a',
    successSoft: '#f0fdf4',
  };

  // Route guard
  useEffect(() => {
    const draft = getMyDataDraft();
    if (draft?.phase === 'analysis-ready') {
      navigate(basePath + '/mydata/connect/analysis-ready', { replace: true });
      return;
    }
    const phases = ['consent', 'guide', 'fetching'];
    if (!draft || !draft.phase || phases.indexOf(draft.phase) < phases.indexOf('guide')) {
      navigate(basePath + '/mydata/connect/consent', { replace: true });
    }
  }, [basePath, navigate]);

  // Timer
  useEffect(() => {
    const tick = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  // collect-all Job 생성 및 상태 polling
  useEffect(() => {
    let draft = getMyDataDraft();
    const session = draft?.nhisSession;
    const parsedStdPatientId = Number(draft?.stdPatientId);
    if (!session) return;
    if (!Number.isInteger(parsedStdPatientId) || parsedStdPatientId <= 0) {
      navigate(basePath + '/mydata/connect/error?phase=fetching&reason=server', { replace: true });
      return;
    }

    const controller = new AbortController();
    let pollTimerId = null;
    let closed = false;

    function goToError(reason) {
      if (closed) return;
      navigate(basePath + `/mydata/connect/error?phase=fetching&reason=${reason}`, { replace: true });
    }

    function patchJobDraft(snapshot, extra = {}) {
      patchMyDataDraft({
        phase: 'fetching',
        stdPatientId: parsedStdPatientId,
        collectJobId: snapshot.jobId || undefined,
        collectJobStatus: snapshot.status,
        collectJobStep: snapshot.currentStep,
        collectJobProgressPercent: snapshot.progressPercent,
        collectJobUpdatedAt: snapshot.updatedAt || new Date().toISOString(),
        ...extra,
      });
    }

    async function finalizeSuccess(snapshot, { alreadyTerminalized = false } = {}) {
      if (closed) return;

      if (!alreadyTerminalized) {
        patchJobDraft(snapshot, {
          phase: 'fetching',
          lastCollectJobId: snapshot.jobId,
          collectJobId: undefined,
          collectJobFinalizing: true,
          collectJobErrorCode: undefined,
          collectJobErrorMessage: undefined,
        });
      }

      let latestCollection = null;
      try {
        latestCollection = await nhisGetLatestCollection();
      } catch (latestErr) {
        console.warn('[MyDataFetching] latest collection 조회 실패:', latestErr);
      }

      if (closed) return;

      try {
        await nhisLogout({ session, stdPatientId: parsedStdPatientId });
      } catch (logoutErr) {
        console.warn('[MyDataFetching] nhisLogout 실패:', logoutErr);
      }

      if (closed) return;
      patchJobDraft(snapshot, {
        phase: 'analysis-ready',
        lastCollectJobId: snapshot.jobId,
        collectJobId: undefined,
        collectJobFinalizing: undefined,
        collectedAt: new Date().toISOString(),
        latestCollection,
        latestCollectionCheckedAt: new Date().toISOString(),
      });
      navigate(basePath + '/mydata/connect/analysis-ready', { replace: true });
    }

    async function handleSnapshot(job) {
      const snapshot = normalizeJobSnapshot(job);
      if (closed) return;

      setJobSnapshot(snapshot);

      if (!snapshot.jobId && !TERMINAL_STATUSES.has(snapshot.status)) {
        goToError('server');
        return;
      }

      if (snapshot.status === 'SUCCEEDED') {
        await finalizeSuccess(snapshot);
        return;
      }

      if (TERMINAL_STATUSES.has(snapshot.status)) {
        patchJobDraft(snapshot, {
          lastCollectJobId: snapshot.jobId,
          collectJobId: undefined,
          collectJobErrorCode: snapshot.errorCode,
          collectJobErrorMessage: snapshot.errorMessage,
        });
        goToError(statusToErrorReason(snapshot.status));
        return;
      }

      patchJobDraft(snapshot);
      pollTimerId = window.setTimeout(() => {
        pollJob(snapshot.jobId);
      }, POLL_INTERVAL_MS);
    }

    async function pollJob(jobId) {
      if (closed || !jobId) return;
      try {
        const job = await nhisGetCollectAllJob(jobId, { signal: controller.signal });
        await handleSnapshot(job);
      } catch (err) {
        const parsed = parseNhisError(err);
        if (closed || parsed.type === 'aborted') return;

        const reason =
          parsed.type === 'timeout' ? 'timeout' :
          parsed.type === 'network' ? 'network' :
          'server';
        goToError(reason);
      }
    }

    async function startOrResumeJob() {
      try {
        if (draft?.phase === 'analysis-ready') {
          navigate(basePath + '/mydata/connect/analysis-ready', { replace: true });
          return;
        }

        if (draft?.collectJobFinalizing === true) {
          const terminalSnapshot = normalizeJobSnapshot({
            jobId: draft.lastCollectJobId,
            status: 'SUCCEEDED',
            currentStep: draft.collectJobStep || 'COMPLETED',
            progressPercent: draft.collectJobProgressPercent ?? 100,
            updatedAt: draft.collectJobUpdatedAt,
          });
          if (!terminalSnapshot.jobId) {
            goToError('server');
            return;
          }
          setJobSnapshot(terminalSnapshot);
          await finalizeSuccess(terminalSnapshot, { alreadyTerminalized: true });
          return;
        }

        const now = new Date().toISOString();
        draft = patchMyDataDraft({
          phase: 'fetching',
          fetchStartedAt: draft?.fetchStartedAt || now,
          stdPatientId: parsedStdPatientId,
        });

        if (draft?.collectJobId) {
          await pollJob(draft.collectJobId);
          return;
        }

        const job = await nhisStartCollectAllJob(
          { session, stdPatientId: parsedStdPatientId },
          { signal: controller.signal },
        );
        await handleSnapshot(job);
      } catch (err) {
        const parsed = parseNhisError(err);
        if (closed || parsed.type === 'aborted') return;

        const reason =
          parsed.type === 'timeout' ? 'timeout' :
          parsed.type === 'network' ? 'network' :
          'server';
        goToError(reason);
      }
    }

    startOrResumeJob();

    return () => {
      closed = true;
      if (pollTimerId) window.clearTimeout(pollTimerId);
      controller.abort();
    };
  }, [basePath, navigate]);

  // Beforeunload warning
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  const statusLabel = STATUS_LABELS[jobSnapshot.status] || STATUS_LABELS.RUNNING;
  const displayProgress = getDisplayProgress(jobSnapshot);

  return (
    <MyDataConnectLayout step={1}>
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes dotPulse {
          0%, 80%, 100% { transform: scale(0.75); opacity: 0.35; }
          40% { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* 2단계 안내 박스 */}
      <div style={{
        background: tone.surface, border: `1px solid ${tone.border}`, borderRadius: 12,
        padding: '14px 16px', marginBottom: 16,
      }}>
        <p style={{ fontSize: 15, fontWeight: 700, color: tone.textStrong, marginBottom: 12 }}>
          2단계 의료 데이터 수집
        </p>
        <p style={{ margin: 0, fontSize: 13, color: tone.text, lineHeight: 1.6 }}>
          건강검진, 진료, 투약 데이터를 순차적으로 수집하고 있습니다.
        </p>
        <p style={{ margin: '4px 0 10px', fontSize: 12, color: tone.textMuted, lineHeight: 1.6 }}>
          수집이 오래 걸려도 화면은 주기적으로 진행 상태를 확인합니다.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {COLLECT_ITEMS.map((item) => (
            <span key={item.label} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 20,
              background: tone.surfaceMuted, color: tone.text, fontWeight: 600,
              border: `1px solid ${tone.border}`,
            }}>
              {item.label}
            </span>
          ))}
        </div>
      </div>

      <div role="status" aria-live="polite" aria-busy="true" style={{ textAlign: 'center', padding: '20px 0 28px' }}>
        {basePath !== '/app' && (
          <h1 className="page-title" style={{ marginBottom: 6, fontSize: 24 }}>의료 데이터를 수집하고 있습니다</h1>
        )}
        <p className="page-description" style={{ marginBottom: 28 }} aria-live="polite">
          {statusLabel}
        </p>

        {/* Spinner */}
        <div aria-hidden="true" style={{
          margin: '0 auto 26px',
          width: 'fit-content',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        }}>
          {[0, 1, 2].map((idx) => (
            <span
              key={idx}
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: tone.primary,
                animation: 'dotPulse 1.2s ease-in-out infinite',
                animationDelay: `${idx * 0.16}s`,
              }}
            />
          ))}
        </div>

        <div
          aria-label={`수집 진행률 ${Math.round(displayProgress)}%`}
          style={{
            maxWidth: 400,
            height: 8,
            margin: '0 auto 18px',
            borderRadius: 999,
            background: tone.surfaceMuted,
            border: `1px solid ${tone.border}`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${displayProgress}%`,
              height: '100%',
              borderRadius: 999,
              background: tone.primary,
              transition: 'width 0.3s ease',
            }}
          />
        </div>

        {/* 수집 항목 카드 */}
        <div role="list" style={{
          maxWidth: 400, margin: '0 auto 24px',
          display: 'flex', flexDirection: 'column', gap: 10,
        }}>
          {COLLECT_ITEMS.map((item) => {
            const state = getStepState(item.step, jobSnapshot.currentStep, jobSnapshot.status, elapsed);
            const active = state === 'active' || state === 'done';
            const done = state === 'done';
            return (
              <div
                key={item.label}
                role="listitem"
                aria-label={`${item.label} ${done ? '완료' : active ? '확인 중' : '대기 중'}`}
                style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 18px', borderRadius: 10,
                border: `1px solid ${done ? tone.success : active ? tone.primary : tone.border}`,
                background: done ? tone.successSoft : active ? tone.primarySoft : tone.surfaceMuted,
                animation: active ? 'fadeInUp 0.4s ease' : 'none',
                transition: 'all 0.3s',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 20, height: 20, borderRadius: '50%',
                    background: done ? tone.success : active ? tone.primary : tone.border,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {active && <span style={{ color: '#fff', fontSize: 12, lineHeight: 1 }}>✓</span>}
                  </div>
                  <span style={{ fontSize: 15, fontWeight: 600, color: active ? tone.textStrong : tone.textMuted }}>
                    {item.label}
                  </span>
                </div>
                <span style={{
                  fontSize: 12, color: done ? tone.success : active ? tone.primary : tone.textMuted,
                }}>
                  {done ? '완료' : active ? '확인 중' : '대기 중'}
                </span>
              </div>
            );
          })}
        </div>

        {/* 안내 문구 */}
        <div style={{
          maxWidth: 400, margin: '0 auto 16px', padding: '12px 16px',
          background: tone.surfaceMuted, borderRadius: 8, border: `1px solid ${tone.border}`,
        }}>
          <p style={{ fontSize: 13, color: tone.textMuted, margin: 0, lineHeight: 1.6 }}>
            수집 도중 화면을 이탈하면 처음부터 다시 진행해야 합니다.
          </p>
        </div>

      </div>

    </MyDataConnectLayout>
  );
}
